import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ClipboardCheck, CheckCircle2, XCircle, User2, Calendar, Hash, Tag,
} from 'lucide-react';

const STATUS_COLOR = {
  Pending: 'border-amber-500/40 text-amber-200',
  Approved: 'border-emerald-500/40 text-emerald-300',
  Rejected: 'border-red-500/40 text-red-300',
};

export default function FormatApprovalsPage() {
  const [tab, setTab] = useState('Pending');
  const [items, setItems] = useState([]);
  const [decideOpen, setDecideOpen] = useState(null); // suggestion being decided
  const [finalName, setFinalName] = useState('');

  const load = async () => {
    const params = tab === 'all' ? '' : `?status=${tab}`;
    const res = await api.get(`/format-suggestions${params}`);
    setItems(res.data);
  };
  useEffect(() => { load(); }, [tab]);

  const openDecide = (s) => {
    setDecideOpen(s);
    setFinalName(s.suggested_name);
  };

  const decide = async (decision) => {
    if (!decideOpen) return;
    const payload = { decision };
    if (decision === 'Approved') {
      payload.final_name = finalName.trim() || decideOpen.suggested_name;
    }
    try {
      await api.post(`/format-suggestions/${decideOpen.id}/decision`, payload);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save decision');
      return;
    }
    setDecideOpen(null);
    setFinalName('');
    load();
  };

  return (
    <div>
      <PageHeader
        icon={ClipboardCheck}
        title="Format Approvals"
        subtitle="Review custom work formats suggested by the team. Approving adds it to the global format dropdown."
      />

      <Card className="shine-card p-3 rounded-xl mb-4 flex flex-wrap gap-2">
        {['Pending', 'Approved', 'Rejected', 'all'].map((t) => (
          <Button
            key={t} size="sm" variant={tab === t ? 'default' : 'outline'} onClick={() => setTab(t)}
            className={tab === t ? 'bg-amber-500 hover:bg-amber-400 text-[#0a1628]' : 'border-amber-500/40 text-amber-200'}
            data-testid={`fa-tab-${t}`}
          >
            {t === 'all' ? 'All' : t}
          </Button>
        ))}
      </Card>

      {items.length === 0 ? (
        <Card className="shine-card p-10 text-center text-slate-400 rounded-xl">
          {tab === 'Pending' ? 'No pending suggestions. 🎉' : `No ${tab.toLowerCase()} suggestions yet.`}
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <Card key={s.id} className="shine-card p-4 rounded-xl" data-testid={`fa-row-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className={STATUS_COLOR[s.status]}>{s.status}</Badge>
                    <Tag className="h-4 w-4 text-amber-300" />
                    <h3 className="font-bold text-white text-lg">{s.suggested_name}</h3>
                    {s.final_name && s.final_name !== s.suggested_name && (
                      <span className="text-xs text-emerald-300">→ approved as <span className="font-semibold">{s.final_name}</span></span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    <span className="inline-flex items-center gap-1"><User2 className="h-3 w-3" /> {s.suggested_by_name}</span>
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(s.created_at).toLocaleDateString()}</span>
                    <span className="inline-flex items-center gap-1"><Hash className="h-3 w-3" /> {(s.entry_ids || []).length} entr{(s.entry_ids || []).length === 1 ? 'y' : 'ies'} pending rename</span>
                  </div>
                  {s.reviewed_by_name && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      Decided by {s.reviewed_by_name} on {new Date(s.reviewed_at).toLocaleString()}
                    </div>
                  )}
                </div>
                {s.status === 'Pending' && (
                  <Button onClick={() => openDecide(s)} className="bg-amber-500 hover:bg-amber-400 text-[#0a1628] font-semibold" data-testid={`fa-decide-${s.id}`}>
                    Review
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!decideOpen} onOpenChange={(o) => !o && (setDecideOpen(null), setFinalName(''))}>
        <DialogContent className="bg-[#0a1628] border-amber-500/20 text-slate-100 max-w-md">
          {decideOpen && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white">Review “{decideOpen.suggested_name}”</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-slate-400">
                  Suggested by <span className="text-amber-200">{decideOpen.suggested_by_name}</span> ·
                  {' '}{(decideOpen.entry_ids || []).length} entr{(decideOpen.entry_ids || []).length === 1 ? 'y' : 'ies'} will be renamed.
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 mb-1 block">Final name (you can rename for consistency)</label>
                  <Input value={finalName} onChange={(e) => setFinalName(e.target.value)} className="bg-[#0f2240] border-amber-500/20" data-testid="fa-final-name" />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Approving adds this to the global format dropdown for everyone going forward.
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button onClick={() => decide('Rejected')} variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="fa-reject">
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
                <Button onClick={() => decide('Approved')} className="bg-emerald-500 hover:bg-emerald-400 text-[#0a1628]" data-testid="fa-approve">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
